import { MouseEvent, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import Product from "../../types/Product";

interface InfoProperty {
  key: keyof Product;
  label: string;
}

interface ProductInfoProps {
  product: Product;
}

const infoProperties: InfoProperty[] = [
  { key: "nr_ncm", label: "NCM" },
  { key: "ds_lista", label: "Lista" },
  { key: "nr_cest_mercadoria", label: "CEST" },
  { key: "ds_marca", label: "Ind/Marca" },
  { key: "ds_tipo_mercadoria", label: "Tipo" },
  { key: "ds_grupo_categoria", label: "Grupo de categoria" },
  { key: "ds_categoria", label: "Categoria" },
  { key: "ds_subcategoria", label: "Subcategoria" },
  { key: "ean_venda_mercadoria", label: "Código de barras" },
];

function ProductInfo({ product }: ProductInfoProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `product-${product.cd_mercadoria}-popover` : undefined;

  return (
    <>
      <IconButton onClick={handleClick} type="button">
        <InfoIcon color="primary" />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Card
          sx={{
            "& .MuiCardHeader-root": {
              padding: "0.25rem",
            },
            "& .MuiCardHeader-action": {
              margin: 0,
            },
            "& .MuiCardContent-root": {
              padding: "0.25rem",
            },
          }}
        >
          <CardHeader
            disableTypography
            action={
              <IconButton size="small" onClick={handleClose} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            title={
              <Typography variant="subtitle2">
                {product.ds_apresentacao}
              </Typography>
            }
          />
          <Divider />
          <CardContent>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  border: "none",
                  padding: "0 0.5rem",
                },
              }}
              size="small"
            >
              <TableBody>
                {infoProperties.map(propertie => (
                  <TableRow key={propertie.key}>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {propertie.label}:
                      </Typography>
                    </TableCell>
                    <TableCell>{product[propertie.key] as string}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="subtitle2">Embalagens:</Typography>
                  </TableCell>
                  <TableCell>
                    {product.embalagens
                      ?.map(
                        emabalagem =>
                          `${emabalagem?.ds_tipo || ""} ${emabalagem?.qt_embalagem || ""}`
                      )
                      .join(",")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="subtitle2">
                      Preço Sugestão Consumidor:
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {product.precoSugerido?.vl_conexao_preco_consumidor
                        ? new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(
                            product.precoSugerido.vl_conexao_preco_consumidor
                          )
                        : "sem sugestão"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Typography variant="subtitle2">Preço</Typography>

            <Table
              sx={{
                border: "1px solid rgba(224, 224, 224, 1)",
                "& .MuiTableHead-root": {
                  backgroundColor: "rgba(224, 224, 224, 1)",
                },
              }}
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Preço Líquido</TableCell>
                  <TableCell>Imposto</TableCell>
                  <TableCell>Líquido + Imposto</TableCell>
                  <TableCell>PMC</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{product.infos.vl_preco_liq_formated}</TableCell>
                  <TableCell>{product.infos.vl_imposto_formated}</TableCell>
                  <TableCell>{product.infos.vl_total_formated}</TableCell>
                  <TableCell>
                    {product.infos.vl_preco_maximo_consumidor_formated}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}

export default ProductInfo;
